import { useState, type FormEvent } from 'react';
import { EMPTY_STATES } from '@saesak/shared';
import { ApiError } from '@/lib/api';
import { Button, Field, Input } from '@/components/ui';
import { useAuth } from '@/store/auth';
import { AuthLayout } from './AuthLayout';

/**
 * 로그인은 했지만 아직 기관에 연결되지 않은 상태.
 * 초대코드를 넣어 역할·시설·(학부모라면) 원아까지 자동 연결한다 (§4, §5.1).
 */
export function OnboardingPage() {
  const redeemInvite = useAuth((s) => s.redeemInvite);
  const logout = useAuth((s) => s.logout);
  const userName = useAuth((s) => s.user?.name ?? '');

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await redeemInvite(code.trim().toUpperCase());
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : '연결이 잠시 불안정해요. 다시 시도해 주세요.',
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title={`${userName}님, 거의 다 왔어요`}
      description={EMPTY_STATES.children.body}
      footer={
        <button type="button" className="underline" onClick={() => void logout()}>
          다른 계정으로 로그인
        </button>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="초대코드" required>
          <Input
            required
            autoCapitalize="characters"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ABCD1234"
            className="tracking-[0.2em]"
          />
        </Field>

        {error && (
          <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
            {error}
          </p>
        )}

        <Button type="submit" block size="lg" loading={loading}>
          연결하기
        </Button>
      </form>
    </AuthLayout>
  );
}
